



















































import { Component, Prop } from 'vue-property-decorator';
import 'devextreme/data/odata/store';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { ContextsState } from '@/store';
import DataSource from 'devextreme/data/data_source';
import {
    Table,
    TableOptions,
    removeItemsFromView,
    getTableCaption,
} from '@/core/components/table';
import { Page } from '@/core/components/controls';
import { DxDataGrid } from 'devextreme-vue/data-grid';
import DxSelectBox from 'devextreme-vue/select-box';
import { localize } from '@/core/filters';
import {
    CONTRACTS,
    WindowsServiceContract,
    FetchServiceContract,
    EventsServiceContract,
    Events
} from '@/core/contracts';
import { guid } from '@/core/helpers';
import { InactivityInterval, Vehicle, Crane, Site, Resource } from '@/dto';
import { IContext } from '@/api/dto';
import UnavailabilityIntervalsListTable from './UnavailabilityIntervalsListTable.vue';
import { UnavailabilityIntervalsArgs, TargetTypes } from './types';
import DevExpress from 'devextreme/bundles/dx.all';

@Component({
    components: {
        DxDataGrid,
        DxSelectBox,
        UnavailabilityIntervalsListTable,
        TableOptions
    },
})
export default class UnavailabilityIntervalListPage extends Page {
    @Prop({ default: () => ({}) })
    public args!: UnavailabilityIntervalsArgs | null;
    public table: Table | null = null;
    public tableKey: string = 'unavailabilityInterval-control';

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;
    @Inject(CONTRACTS.FetchService)
    private _fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;

    private typeUpdated: boolean = false;
    private selectedTypeName: string = 'Vehicle';
    private vehicles: Vehicle[] = [];
    private cranes: Crane[] = [];
    private resources: Resource[] = [];

    public setTable(table: Table): void {
        this.table = table;
    }

    public async mounted() {
        this.selectedTypeName = this.selectedType;
        this._eventsService!.on(
            Events.ProcessingFinished,
            this.refreshTable
        );
        this._eventsService!.on(
            Events.ProcessingStarted,
            this.refreshTable
        );
    }

    public async beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.refreshTable
        );
        this._eventsService!.off(
            Events.ProcessingStarted,
            this.refreshTable
        );
    }

    public get dataSource(): DataSource {
        return new DataSource({
            key: 'id' as keyof InactivityInterval,
            load: this.load,
            insert: (inactivityInterval: InactivityInterval) => this.insert(inactivityInterval),
            update: (id: string, data: InactivityInterval) => this.update(id, data),
        });
    }

    public get tableName(): string {
        return getTableCaption(this.tableKey);
    }

    public get isRowSelected() {
        return this.table && this.table!.getSelectedRowKeys().length === 1;
    }

    public addUnavailabilityInterval() {
        this.table!.addRow();
    }

    public editUnavailabilityInterval() {
        const selectedKey = this.table!.getSelectedRowKeys()[0];

        if (selectedKey) {
            const selectedIndex = this.table!.getRowIndexByKey(selectedKey);
            this.table!.editRow(selectedIndex);
        }
    }

    public get localize() {
        return localize;
    }

    private get tableInstance(): UnavailabilityIntervalsListTable {
        return this.$refs['table-component'] as UnavailabilityIntervalsListTable;
    }

    private get selectInstance() {
        return this.$refs['select-component'] as DxSelectBox;
    }

    private get getTypes() {
        const items: any[] = [];

        for (const item in TargetTypes) {
            if (isNaN(Number(item))) {
                items.push({
                    name: this.localize('UnavailabilityInterval_' + item),
                    value: item
                });
            }
        }

        return items;
    }

    private deleteUnavailabilityInterval() {
        this.tableInstance.deleteUnavailabilityInterval();
    }

    private get selectedAlias() {
        if (this.args && this.args.alias && this.args.alias !== '') {
            return this.args.alias;
        } else {
            return null;
        }
    }

    private get selectedType() {
        if (this.args && this.args.type && !this.typeUpdated) {
            return this.args.type;
        } else {
            return this.selectInstance ?
                    this.selectedTypeName : TargetTypes.Vehicle.toString();
        }
    }

    private async load() {
        const currentContextId = ContextsState.currentContextId;
        const selectedAlias: string | null = this.selectedAlias;

        const url = `/files/contexts/${currentContextId}/InactivityIntervals`;
        const data = await this._fetchService!.get<IContext>(url);
        let result: any[] = [];

        switch (this.selectedType) {
            case TargetTypes.Vehicle:
                const carUrl = `/files/contexts/${currentContextId}/Vehicles`;
                const vehicles = await this._fetchService!.get<IContext>(carUrl);
                this.vehicles = vehicles.vehicles ? vehicles.vehicles : [];

                result = selectedAlias ?
                        data.inactivityIntervals!.filter(f => f.vehicleId === selectedAlias) :
                        data.inactivityIntervals!.filter(f => f.vehicleId);
                break;
            case TargetTypes.Crane:
                const craneUrl = `/files/contexts/${currentContextId}/Cranes`;
                const cranes = await this._fetchService!.get<IContext>(craneUrl);
                this.cranes = cranes.cranes ? cranes.cranes : [];

                result = selectedAlias ?
                        data.inactivityIntervals!.filter(f => f.craneId === selectedAlias) :
                        data.inactivityIntervals!.filter(f => f.craneId);
                break;
            case TargetTypes.Resource:
                const resourcesUrl = `/files/contexts/${currentContextId}/Resources`;
                const resources = await this._fetchService!.get<IContext>(resourcesUrl);
                this.resources = resources.resources ? resources.resources : [];

                result = selectedAlias ?
                        data.inactivityIntervals!.filter(f => f.resourceId === selectedAlias) :
                        data.inactivityIntervals!.filter(f => f.resourceId);
                break;
            default:
                break;
        }

        return result ? result : [];
    }

    private refreshTable({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.table!.refresh();
        }
    }

    private async insert(inactivityInterval: InactivityInterval) {
        if (this.selectedAlias) {
            switch (this.selectedType) {
                case TargetTypes.Vehicle:
                    inactivityInterval.carId = this.selectedAlias;
                    break;
                case TargetTypes.Crane:
                    inactivityInterval.craneId = this.selectedAlias;
                    break;
                case TargetTypes.Resource:
                    inactivityInterval.resourceId = this.selectedAlias;
                    break;
                default:
                    break;
            }
        }
        inactivityInterval.id = guid();
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        const data = {
            InactivityIntervals_Partial: [inactivityInterval],
        };
        return await this._fetchService!.post<InactivityInterval>(
            url,
            JSON.stringify(data)
        );
    }

    private async update(id: string, inactivityInterval: InactivityInterval) {
        if (this.selectedAlias) {
            switch (this.selectedType) {
                case TargetTypes.Vehicle:
                    inactivityInterval.carId = this.selectedAlias;
                    break;
                case TargetTypes.Crane:
                    inactivityInterval.craneId = this.selectedAlias;
                    break;
                case TargetTypes.Resource:
                    inactivityInterval.resourceId = this.selectedAlias;
                    break;
                default:
                    break;
            }
        }
        const currentContextId = ContextsState.currentContextId;
        const url = `/files/contexts/${currentContextId}?ignoreWarnings=true`;
        inactivityInterval.id = id;
        const data = {
            InactivityIntervals_Partial: [inactivityInterval],
        };
        return await this._fetchService!.post<InactivityInterval>(
            url,
            JSON.stringify(data)
        );
    }
}
